import { useRouter } from 'next/router';
import {
    MobileAlertPanelWrapper,
    TabletText,
    MobileText,
    Divider
} from './AlertPanelMobile.styles';
import { FC, useContext, MouseEvent } from 'react';
import { SearchContext } from '../SearchContext';
import triggerTracking from '@blueprint/tracking';
import { alertLinkClickHandler } from '../common/alertLinkClickHandler';
import { Links } from '../SearchContext';
import { Icon, ButtonLinkWithIcon } from '@blueprint/ui';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';

const mobileCreateAlertButtonText = 'Create alert';

const tabletCreateAlertButtonText = 'Create email alert';
const mobileSaveSearchButtonText = 'Save search';
const tabletSaveSearchButtonText = 'Save this search';
const mobileTurnOnAlertButtonText = 'Turn on alerts';
const tabletTurnOnAlertButtonText = 'Turn on email alert';
const mobileEditSearchButtonText = 'Edit Search';
const tabletEditSearchButtonText = 'Edit saved search';

type AlertPanelMobileProps = {
    links: Links;
};

export const AlertPanelMobile: FC<AlertPanelMobileProps> = ({ links }) => {
    const { state } = useContext(SearchContext);
    const router = useRouter();
    const isLoggedIn = useIsLoggedIn();

    const { userAlertId, savedSearchAndAlerts } = state;

    const onClick = (
        evt: MouseEvent<HTMLAnchorElement>,
        label?: string,
        requiresAuth = false
    ) => {
        evt.preventDefault();

        if (label) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: label
            });
        }

        alertLinkClickHandler({
            evt,
            router,
            requiresAuth,
            loggedIn: !!isLoggedIn
        });
    };

    const isSavedSearch = userAlertId !== null;

    return (
        <>
            {isSavedSearch ? (
                <MobileAlertPanelWrapper data-testid="alert-panel-mobile">
                    <ButtonLinkWithIcon
                        variant="tertiary"
                        data-testid="turn-on-alert"
                        href={savedSearchAndAlerts?.uri as string}
                        data-url={savedSearchAndAlerts?.uri as string}
                        onClick={(evt) =>
                            onClick(
                                evt,
                                savedSearchAndAlerts?.currentFrequency.value ===
                                    ''
                                    ? 'alert_started'
                                    : 'edit_alert',
                                true
                            )
                        }
                    >
                        <Icon iconId="bell" />{' '}
                        <MobileText data-testid="display-mobile-text">
                            {mobileTurnOnAlertButtonText}
                        </MobileText>
                        <TabletText data-testid="display-tablet-text">
                            {tabletTurnOnAlertButtonText}
                        </TabletText>
                    </ButtonLinkWithIcon>

                    <Divider />
                    <ButtonLinkWithIcon
                        variant="tertiary"
                        data-testid="edit-search"
                        href={savedSearchAndAlerts?.uri as string}
                        data-url={savedSearchAndAlerts?.uri as string}
                        onClick={(evt) => onClick(evt, 'edit_search', true)}
                    >
                        <Icon iconId="pencil" />{' '}
                        <MobileText>{mobileEditSearchButtonText}</MobileText>
                        <TabletText>{tabletEditSearchButtonText}</TabletText>
                    </ButtonLinkWithIcon>
                </MobileAlertPanelWrapper>
            ) : (
                <MobileAlertPanelWrapper data-testid="alert-panel-mobile">
                    <ButtonLinkWithIcon
                        variant="tertiary"
                        data-testid="create-alert"
                        href={links?.createAlert || ''}
                        data-url={links?.createAlert || ''}
                        onClick={(evt) => onClick(evt, 'alert_started', true)}
                    >
                        <Icon iconId="bell" />{' '}
                        <MobileText>{mobileCreateAlertButtonText}</MobileText>
                        <TabletText>{tabletCreateAlertButtonText}</TabletText>
                    </ButtonLinkWithIcon>

                    <Divider />

                    <ButtonLinkWithIcon
                        variant="tertiary"
                        data-testid="save-search"
                        href={links?.saveSearch || ''}
                        data-url={links?.saveSearch || ''}
                        onClick={(evt) => onClick(evt, 'search_started', true)}
                    >
                        <Icon iconId="save_outline" />{' '}
                        <MobileText>{mobileSaveSearchButtonText}</MobileText>
                        <TabletText>{tabletSaveSearchButtonText}</TabletText>
                    </ButtonLinkWithIcon>
                </MobileAlertPanelWrapper>
            )}
        </>
    );
};
